import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { TriangleAlert } from 'lucide-react';

import { useBanner } from '@/lib/banner';

import { api, type Painel } from './api';

/** A âncora da seção "Registros faltantes" do painel, para onde "Ver máquinas" leva. */
export const ANCORA_DOS_REGISTROS = 'registros-faltantes';

/**
 * O texto do banner das máquinas operando sem apontamento (p.33: "o texto diz
 * a condição e o tamanho dela, com número").
 *
 * O DADO NÃO É O DO EXEMPLO DA p.33. O manual escreve "sem leitura de
 * horímetro há mais de 48 horas"; o que o servidor sabe hoje (`GET /painel`,
 * bloco `registros`) é a máquina com a situação Operando e NENHUM TURNO
 * APONTADO nos últimos dois dias de calendário (`dia >= hoje - 2`). O banner
 * diz o que o dado diz — turno apontado, e não leitura de horímetro.
 *
 * A lista vem cortada em 10: com 10, a frase diz "10 ou mais", e nunca lê a
 * lista cortada como total (o erro que o H7 vigia).
 */
export function textoDoBannerDeApontamento(p: Painel): string | null {
  if (!p.registros.permitido) return null;
  const n = p.registros.operandoSemApontamento.length;
  if (n === 0) return null;
  if (n >= 10) return '10 ou mais máquinas operando estão sem turno apontado há mais de 2 dias.';
  if (n === 1) return '1 máquina operando está sem turno apontado há mais de 2 dias.';
  return `${String(n)} máquinas operando estão sem turno apontado há mais de 2 dias.`;
}

/**
 * Registra o banner de ATENÇÃO enquanto a tela (o painel, a frota) está
 * aberta e há máquina na condição. "Ver máquinas" leva à lista delas, na
 * seção Registros faltantes do painel.
 *
 * A consulta é a do painel, com a mesma chave: no painel não pede de novo; na
 * frota, pede uma vez e se atualiza no mesmo ritmo do painel.
 */
export function useBannerDeApontamento(): void {
  const navegar = useNavigate();
  const painel = useQuery({ queryKey: ['painel'], queryFn: () => api.painel(), refetchInterval: 5 * 60_000 });
  const texto = painel.data ? textoDoBannerDeApontamento(painel.data) : null;
  useBanner(
    texto === null
      ? null
      : {
          /* O id leva o texto: dispensar 3 máquinas não cala a quarta. */
          id: `apontamento:${texto}`,
          gravidade: 'atencao',
          icone: TriangleAlert,
          texto,
          acao: {
            rotulo: 'Ver máquinas',
            aoAcionar: () => {
              void navegar({ to: '/painel', hash: ANCORA_DOS_REGISTROS }).then(() => {
                rolarParaOsRegistros();
              });
            },
          },
          rotuloDeFechar: 'Fechar o aviso de máquinas sem apontamento',
        },
  );
}

/** Leva a seção à vista e o foco a ela, para o teclado continuar dali. */
export function rolarParaOsRegistros(): void {
  const secao = document.getElementById(ANCORA_DOS_REGISTROS);
  if (!secao) return;
  secao.scrollIntoView({ block: 'start', behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
  secao.focus({ preventScroll: true });
}
